"use client";

import { formattingLocale, resolveLocale } from "@webdo/i18n";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type FormEvent,
    type ReactNode,
} from "react";
import {
    bookingPortalAddDays,
    bookingPortalAddMonths,
    bookingPortalApiBasePath,
    bookingPortalPublicBookingPayload,
    bookingPortalSlotsSearchParams,
    bookingPortalCalendarDays,
    bookingPortalDateFormatter,
    bookingPortalDateFromDayKey,
    bookingPortalDayHourKey,
    bookingPortalDayKey,
    bookingPortalStartOfWeek,
    bookingPortalStartOfMonth,
    bookingPortalTimeParts,
    bookingPortalWeekDays,
    bookingPortalZonedDateTime,
    indexBookingPortalSlotProjections,
    indexBookingPortalSlots,
    mergeBookingPortalSlots,
    normalizeBookingPortalTimezone,
    BOOKING_PORTAL_VARIANT_CONTRACTS,
    BOOKING_PORTAL_VARIANT_BLUEPRINTS,
    type BookingPortalFlowStep,
    type BookingPortalSlotProjection,
    type PublicBookingPortal,
} from "./bookingPortal";
import {
    bookingPortalProfileContract,
    normalizeBookingPortalProfile,
    normalizeBookingPortalVariantForProfile,
    type BookingPortalProfile,
} from "./bookingPortalProfiles";
import { BOOKING_PORTAL_CSS } from "./bookingPortalStyles";
import { bookingMessages } from "./i18n/booking";
import { bookingDemoMessages } from "./i18n/bookingDemo";
import type { SiteBuilderBlock } from "./siteBuilderTypes";

type BookingResource = {
    uuid: string;
    name: string;
    type: string;
    capacity?: number;
    location?: string | null;
    color?: string | null;
    description?: string | null;
    durationMinutes?: number;
    photoUrl?: string | null;
};

type BookingService = {
    uuid: string;
    name: string;
    durationMinutes: number;
    price: string | null;
    description: string | null;
    resources: BookingResource[];
};

type BookingSlot = {
    startAt: string;
    endAt: string;
    resourceUuid: string | null;
    resourceName: string | null;
    resourceType: string | null;
};

type BookingIconName =
    | "arrow"
    | "calendar"
    | "check"
    | "clock"
    | "compass"
    | "scissors"
    | "sparkles"
    | "user";

function BookingIcon({ name }: { name: BookingIconName }) {
    const content: Record<BookingIconName, ReactNode> = {
        arrow: <><path d="m15 18-6-6 6-6" /><path d="M9 12h10" /></>,
        calendar: <><rect height="18" rx="2" width="18" x="3" y="4" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
        check: <path d="m5 12 4 4L19 6" />,
        clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
        compass: <><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" /></>,
        scissors: <><circle cx="6" cy="7" r="3" /><circle cx="6" cy="17" r="3" /><path d="m8.7 8.4 11.3 6.1M8.7 15.6 20 9.5" /></>,
        sparkles: <><path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3Z" /><path d="m6 14 .8 2.2L9 17l-2.2.8L6 20l-.8-2.2L3 17l2.2-.8L6 14Z" /></>,
        user: <><circle cx="12" cy="8" r="4" /><path d="M4.5 21a7.5 7.5 0 0 1 15 0" /></>,
    };

    return (
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8">
                {content[name]}
            </g>
        </svg>
    );
}

function booleanOption(value: unknown, fallback: boolean): boolean {
    if (value === undefined || value === null || value === "") return fallback;
    return value === true || value === "true" || value === 1 || value === "1";
}

function initials(name: string): string {
    return name
        .split(/\s+/u)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
}

function listFromResponse<T>(payload: unknown): T[] {
    const direct = payload && typeof payload === "object" && "data" in payload
        ? (payload as { data?: unknown }).data
        : payload;

    if (Array.isArray(direct)) return direct as T[];
    if (direct && typeof direct === "object" && Array.isArray((direct as { items?: unknown }).items)) {
        return (direct as { items: T[] }).items;
    }

    return [];
}

function demoServices(
    now: Date,
    languageCode: string | undefined,
    timezone: string,
    profile: BookingPortalProfile,
): { services: BookingService[]; slots: BookingSlot[] } {
    const content = bookingDemoMessages(languageCode);
    const names = content.profileNames[profile];
    const resourceNames = content.resourceNames[profile];
    const resourceType = profile === "restaurant"
        ? "table"
        : profile === "facility"
            ? "court"
            : profile === "vehicle-service"
                ? "equipment"
                : "professional";
    const resources: BookingResource[] = [
        { uuid: "demo-alex", name: resourceNames[0]!, type: resourceType, capacity: 4, location: content.locations[0], color: "#8b5cf6" },
        { uuid: "demo-nina", name: resourceNames[1]!, type: resourceType, capacity: 8, location: content.locations[1], color: "#0ea5e9" },
        { uuid: "demo-sam", name: resourceNames[2]!, type: resourceType, capacity: 12, location: content.locations[2], color: "#10b981" },
    ];
    const services: BookingService[] = [
        { uuid: "demo-service-1", name: names[0]!, durationMinutes: 30, price: "25", description: content.services[0]?.[1] ?? null, resources },
        { uuid: "demo-service-2", name: names[1]!, durationMinutes: 60, price: "45", description: content.services[1]?.[1] ?? null, resources },
        { uuid: "demo-service-3", name: names[2]!, durationMinutes: 45, price: "35", description: content.services[2]?.[1] ?? null, resources },
    ];
    const slots: BookingSlot[] = [];

    for (let day = 0; day < 90; day += 1) {
        for (let resourceIndex = 0; resourceIndex < resources.length; resourceIndex += 1) {
            const resource = resources[resourceIndex]!;
            for (let timeIndex = 0; timeIndex < 3; timeIndex += 1) {
                if ((day + resourceIndex + timeIndex) % 4 === 0) continue;
                const dayKey = bookingPortalDayKey(
                    bookingPortalAddDays(now, day, timezone),
                    timezone,
                );
                const start = bookingPortalZonedDateTime(
                    dayKey,
                    timezone,
                    9 + timeIndex * 3 + resourceIndex,
                    timeIndex === 1 ? 30 : 0,
                );
                slots.push({
                    startAt: start.toISOString(),
                    endAt: new Date(start.getTime() + 30 * 60_000).toISOString(),
                    resourceUuid: resource.uuid,
                    resourceName: resource.name,
                    resourceType: resource.type,
                });
            }
        }
    }

    return { services, slots };
}

function sameSlot(first: BookingSlot | null, second: BookingSlot): boolean {
    return first?.startAt === second.startAt && first.resourceUuid === second.resourceUuid;
}

function formatPortalDate(
    date: Date,
    locale: string,
    timezone: string,
    options: Intl.DateTimeFormatOptions,
): string {
    return bookingPortalDateFormatter(locale, timezone, options).format(date);
}

export function BookingPortalBlock({
    block,
    bookingDemoData = false,
    editorMode,
    languageCode,
    portal,
    readOnly,
}: {
    block: SiteBuilderBlock;
    bookingDemoData?: boolean;
    editorMode: boolean;
    languageCode?: string;
    portal: PublicBookingPortal | null;
    readOnly?: boolean;
}) {
    const language = resolveLocale(languageCode);
    const labels = bookingMessages(language);
    const locale = formattingLocale(language);
    const profile = normalizeBookingPortalProfile(block.options?.bookingProfile);
    const profileContract = bookingPortalProfileContract(profile);
    const variant = normalizeBookingPortalVariantForProfile(
        profile,
        block.options?.bookingVariant,
    );
    const variantContract = BOOKING_PORTAL_VARIANT_CONTRACTS[variant];
    const requiresResourceChoice = profileContract.resourceSelection === "customer-choice";
    const flow = BOOKING_PORTAL_VARIANT_BLUEPRINTS[variant].filter((step) => (
        step !== "resource" || requiresResourceChoice
    ));
    const showPrices = booleanOption(block.options?.showPrices, true);
    const showDescriptions = booleanOption(block.options?.showServiceDescriptions, true);
    const allowAnyResource = booleanOption(block.options?.allowAnyResource, true);
    const previewMode = portal === null && bookingDemoData;
    const timezone = normalizeBookingPortalTimezone(portal?.timezone);
    const [currentInstant, setCurrentInstant] = useState(() => new Date());
    const todayKey = bookingPortalDayKey(currentInstant, timezone);
    const today = useMemo(
        () => bookingPortalDateFromDayKey(todayKey, timezone),
        [timezone, todayKey],
    );
    const preview = useMemo(
        () => demoServices(today, language, timezone, profile),
        [language, profile, timezone, today],
    );
    const [services, setServices] = useState<BookingService[]>(previewMode ? preview.services : []);
    const [slots, setSlots] = useState<BookingSlot[]>(previewMode ? preview.slots : []);
    const [serviceUuid, setServiceUuid] = useState<string | null>(
        previewMode && (
            variantContract.initialSelection === "first-service"
            || profileContract.serviceSelection === "optional"
        )
            ? preview.services[0]?.uuid ?? null
            : null,
    );
    const [resourceUuid, setResourceUuid] = useState<string | null>(
        previewMode && variant === "venue-grid"
            ? preview.services[0]?.resources[0]?.uuid ?? null
            : null,
    );
    const [resourceChosen, setResourceChosen] = useState(previewMode && variant === "venue-grid");
    const [selectedDate, setSelectedDate] = useState(bookingPortalDayKey(today, timezone));
    const [visibleMonth, setVisibleMonth] = useState(() => bookingPortalStartOfMonth(today, timezone));
    const [visibleWeek, setVisibleWeek] = useState(() => bookingPortalStartOfWeek(today, timezone));
    const [dateJumpOpen, setDateJumpOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<BookingSlot | null>(null);
    const [servicesLoading, setServicesLoading] = useState(!previewMode && portal !== null);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [availabilityRevision, setAvailabilityRevision] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [contact, setContact] = useState({ name: "", email: "", phone: "", notes: "" });
    const [partySize, setPartySize] = useState(2);
    const dialogPanelRef = useRef<HTMLDivElement | null>(null);
    const dialogNameRef = useRef<HTMLInputElement | null>(null);
    const slotTriggerRef = useRef<HTMLElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const slotCacheRef = useRef(new Map<string, BookingSlot[]>());
    const calendarDates = useMemo(
        () => bookingPortalCalendarDays(visibleMonth, timezone),
        [timezone, visibleMonth],
    );
    const weekDates = useMemo(
        () => bookingPortalWeekDays(visibleWeek, timezone),
        [timezone, visibleWeek],
    );
    const [calendarFrom, calendarTo] = useMemo(() => {
        if (variant === "venue-grid" || variant === "quick-slots") {
            const from = variant === "venue-grid" ? (weekDates[0] ?? today) : today;
            return [
                from,
                bookingPortalAddDays(from, variant === "venue-grid" ? 7 : 28, timezone),
            ] as const;
        }
        const from = calendarDates[0] ?? today;
        return [from, bookingPortalAddDays(from, calendarDates.length, timezone)] as const;
    }, [calendarDates, timezone, today, variant, weekDates]);
    const capacityUnits = profileContract.capacitySelection === "party-size"
        ? partySize
        : 1;
    const resources = useMemo(() => {
        const unique = new Map<string, BookingResource>();
        services.forEach((service) => service.resources.forEach((resource) => unique.set(resource.uuid, resource)));
        return [...unique.values()];
    }, [services]);
    const selectedService = services.find((service) => service.uuid === serviceUuid) ?? null;
    const selectedResource = resources.find((resource) => resource.uuid === resourceUuid) ?? null;
    const availableServices = variant === "professional" && resourceUuid
        ? services.filter((service) => service.resources.some((resource) => resource.uuid === resourceUuid))
        : services;
    const slotIndex = useMemo(
        () => indexBookingPortalSlots(slots, timezone),
        [slots, timezone],
    );
    const bookableSlotProjections = useMemo(
        () => slotIndex.slots.filter((slot) => slot.startAtMilliseconds > currentInstant.getTime()),
        [currentInstant, slotIndex.slots],
    );
    const resourceFilteredSlotIndex = useMemo(() => indexBookingPortalSlotProjections(
        bookableSlotProjections.filter((slot) => (
            !resourceUuid || slot.slot.resourceUuid === resourceUuid
        )),
    ), [bookableSlotProjections, resourceUuid]);
    const filteredSlotProjections = resourceFilteredSlotIndex.byDay.get(selectedDate) ?? [];
    const isDialogCheckout = profile !== "restaurant" && variantContract.checkout === "dialog";
    const loading = servicesLoading || slotsLoading;
    const currentStage: BookingPortalFlowStep = profile === "restaurant"
        ? selectedSlot === null ? "schedule" : "contact"
        : isDialogCheckout
        ? selectedSlot === null ? "schedule" : "contact"
        : variant === "guided"
        ? selectedService === null
            ? "service"
            : requiresResourceChoice && !resourceChosen
                ? "resource"
                : selectedSlot === null
                    ? "schedule"
                    : "contact"
        : variant === "professional"
            ? !resourceChosen
                ? "resource"
                : selectedService === null
                    ? "service"
                    : selectedSlot === null
                        ? "schedule"
                        : "contact"
            : selectedService === null
                ? "service"
                : selectedSlot === null
                    ? "schedule"
                    : "contact";
    const currentStageIndex = flow.indexOf(currentStage);

    useEffect(() => {
        const ownerWindow = rootRef.current?.ownerDocument.defaultView;
        if (!ownerWindow) return;
        const interval = ownerWindow.setInterval(() => setCurrentInstant(new Date()), 60_000);

        return () => ownerWindow.clearInterval(interval);
    }, []);

    useEffect(() => {
        const previewVenueResourceUuid = previewMode && variant === "venue-grid"
            ? preview.services[0]?.resources[0]?.uuid ?? null
            : null;
        setResourceUuid(previewVenueResourceUuid);
        setResourceChosen(previewMode && variant === "venue-grid");
        setSelectedSlot(null);
        setSelectedDate(bookingPortalDayKey(today, timezone));
        setVisibleMonth(bookingPortalStartOfMonth(today, timezone));
        setVisibleWeek(bookingPortalStartOfWeek(today, timezone));
        setDateJumpOpen(false);
        setError(null);
        setServices(previewMode ? preview.services : []);
        setSlots(previewMode ? preview.slots : []);
        setServicesLoading(!previewMode && portal !== null);
        setSlotsLoading(false);
        setPartySize(2);
        setServiceUuid(
            previewMode && (
                variantContract.initialSelection === "first-service"
                || profileContract.serviceSelection === "optional"
            )
                ? preview.services[0]?.uuid ?? null
                : null,
        );
    }, [portal, preview.services, preview.slots, previewMode, profile, profileContract.serviceSelection, timezone, today, variantContract]);

    useEffect(() => {
        if (previewMode || portal === null) return;

        let active = true;
        setServicesLoading(true);
        setError(null);
        fetch(`${bookingPortalApiBasePath(portal)}/services`, { headers: { Accept: "application/json" } })
            .then(async (response) => {
                if (!response.ok) throw new Error("services");
                return response.json() as Promise<unknown>;
            })
            .then((payload) => {
                if (!active) return;
                const nextServices = listFromResponse<BookingService>(payload);
                setServices(nextServices);
                if (
                    variantContract.initialSelection === "first-service"
                    || profileContract.serviceSelection === "optional"
                ) {
                    const firstService = nextServices[0] ?? null;
                    const firstResource = firstService?.resources[0] ?? null;
                    setServiceUuid(firstService?.uuid ?? null);
                    if (variant === "venue-grid") {
                        setResourceUuid(firstResource?.uuid ?? null);
                        setResourceChosen(firstResource !== null);
                    }
                }
            })
            .catch(() => active && setError(labels.unavailable))
            .finally(() => active && setServicesLoading(false));

        return () => { active = false; };
    }, [labels.unavailable, portal, previewMode, profileContract.serviceSelection, variant, variantContract]);

    useEffect(() => {
        if (previewMode || portal === null || serviceUuid === null) return;
        if (variant === "venue-grid" && resourceUuid === null) {
            setSlots([]);
            setSlotsLoading(false);
            return;
        }

        const from = calendarFrom < today ? today : calendarFrom;
        const to = calendarTo;
        const cacheKey = [
            availabilityRevision,
            portal.uuid,
            serviceUuid,
            resourceUuid ?? "any",
            capacityUnits,
            from.toISOString(),
            to.toISOString(),
        ].join(":");
        let active = true;
        setError(null);
        setSelectedSlot(null);
        const cachedSlots = slotCacheRef.current.get(cacheKey);
        if (cachedSlots) {
            setSlots(cachedSlots);
            setSlotsLoading(false);
            return;
        }

        const controller = new AbortController();
        setSlotsLoading(true);
        setSlots([]);
        const loadSlots = async (): Promise<BookingSlot[]> => {
            const query = bookingPortalSlotsSearchParams({
                serviceUuid,
                resourceUuid,
                capacityUnits,
                from,
                to,
            });
            const response = await fetch(
                `${bookingPortalApiBasePath(portal)}/slots?${query.toString()}`,
                { headers: { Accept: "application/json" }, signal: controller.signal },
            );
            if (!response.ok) throw new Error("slots");
            return listFromResponse<BookingSlot>(await response.json() as unknown);
        };
        loadSlots()
            .then((collection) => {
                if (!active) return;
                const mergedSlots = mergeBookingPortalSlots([collection]);
                slotCacheRef.current.set(cacheKey, mergedSlots);
                setSlots(mergedSlots);
            })
            .catch(() => {
                if (!active || controller.signal.aborted) return;
                setError(labels.bookingFailed);
            })
            .finally(() => active && setSlotsLoading(false));

        return () => {
            active = false;
            controller.abort();
        };
    }, [availabilityRevision, calendarFrom, calendarTo, capacityUnits, labels.bookingFailed, portal, previewMode, resourceUuid, serviceUuid, today, variant]);

    function chooseService(service: BookingService) {
        setServiceUuid(service.uuid);
        if (variant === "venue-grid") {
            const firstResource = service.resources[0] ?? null;
            setResourceUuid(firstResource?.uuid ?? null);
            setResourceChosen(firstResource !== null);
        }
        setSelectedSlot(null);
        setError(null);
        if (variant === "guided") {
            setResourceUuid(null);
            setResourceChosen(service.resources.length === 0);
        }
    }

    function chooseResource(nextUuid: string | null) {
        setResourceUuid(nextUuid);
        setResourceChosen(true);
        setSelectedSlot(null);
        setError(null);
        if (variant === "professional") setServiceUuid(null);
    }

    const chooseSlot = useCallback((slot: BookingSlot, trigger?: HTMLElement | null) => {
        slotTriggerRef.current = trigger ?? null;
        setSelectedSlot(slot);
        setError(null);
    }, []);

    function closeDialog() {
        setSelectedSlot(null);
        setError(null);
        rootRef.current?.ownerDocument.defaultView?.requestAnimationFrame(() => {
            slotTriggerRef.current?.focus();
        });
    }

    useEffect(() => {
        if (!isDialogCheckout || selectedSlot === null) return;
        const ownerDocument = rootRef.current?.ownerDocument;
        if (!ownerDocument) return;
        const previousOverflow = ownerDocument.body.style.overflow;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeDialog();
                return;
            }
            if (event.key !== "Tab") return;
            const focusable = [...(dialogPanelRef.current?.querySelectorAll<HTMLElement>(
                "button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex='-1'])",
            ) ?? [])];
            if (focusable.length === 0) return;
            const first = focusable[0]!;
            const last = focusable[focusable.length - 1]!;
            if (event.shiftKey && ownerDocument.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && ownerDocument.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        ownerDocument.addEventListener("keydown", onKeyDown);
        ownerDocument.body.style.overflow = "hidden";
        ownerDocument.defaultView?.requestAnimationFrame(() => dialogNameRef.current?.focus());
        return () => {
            ownerDocument.removeEventListener("keydown", onKeyDown);
            ownerDocument.body.style.overflow = previousOverflow;
        };
    }, [isDialogCheckout, selectedSlot]);

    function goToStep(step: BookingPortalFlowStep) {
        if (step === "service") {
            setSelectedSlot(null);
            setServiceUuid(null);
            if (variant !== "professional") {
                setResourceUuid(null);
                setResourceChosen(false);
            }
        } else if (step === "resource") {
            setSelectedSlot(null);
            setResourceUuid(null);
            setResourceChosen(false);
            if (variant === "professional") setServiceUuid(null);
        } else if (step === "schedule") {
            setSelectedSlot(null);
        }
        setError(null);
    }

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (editorMode || readOnly || portal === null || selectedService === null || selectedSlot === null) return;
        setSubmitting(true);
        setError(null);
        const bookedSlot = selectedSlot;

        try {
            const response = await fetch(`${bookingPortalApiBasePath(portal)}/bookings`, {
                method: "POST",
                headers: { Accept: "application/json", "Content-Type": "application/json" },
                body: JSON.stringify(bookingPortalPublicBookingPayload({
                    portal,
                    serviceUuid: selectedService.uuid,
                    resourceUuid: selectedSlot.resourceUuid ?? resourceUuid,
                    customerName: contact.name,
                    customerEmail: contact.email,
                    customerPhone: contact.phone || null,
                    startAt: selectedSlot.startAt,
                    capacityUnits,
                    notes: contact.notes || null,
                })),
            });
            if (!response.ok) throw new Error("booking");
            setSlots((current) => current.filter((slot) => !sameSlot(bookedSlot, slot)));
            setSelectedSlot(null);
            setAvailabilityRevision((current) => current + 1);
            setSuccess(true);
        } catch {
            setError(labels.bookingFailed);
        } finally {
            setSubmitting(false);
        }
    }

    function stageLabel(step: BookingPortalFlowStep): string {
        if (step === "service") return labels.chooseService;
        if (step === "resource") return labels.chooseProfessional;
        if (step === "schedule") return labels.chooseTime;
        return labels.contactDetails;
    }

    function progress() {
        return (
            <ol
                aria-label={labels.summary}
                className="site-booking__progress"
                style={{ "--booking-step-count": flow.length } as CSSProperties}
            >
                {flow.map((step, index) => {
                    const complete = index < currentStageIndex;
                    const active = index === currentStageIndex;
                    return (
                        <li data-active={active} data-complete={complete} key={step}>
                            <button
                                aria-current={active ? "step" : undefined}
                                className="site-booking__progress-button"
                                disabled={!complete}
                                onClick={() => goToStep(step)}
                                type="button"
                            >
                                <span className="site-booking__progress-number">
                                    {complete ? <BookingIcon name="check" /> : index + 1}
                                </span>
                                <span className="site-booking__progress-label">{stageLabel(step)}</span>
                            </button>
                        </li>
                    );
                })}
            </ol>
        );
    }

    function intro() {
        const content = profile === "restaurant"
            ? { eyebrow: labels.restaurantEyebrow, title: labels.restaurantTitle, hint: labels.restaurantHint, icon: "calendar" as const }
            : variant === "guided"
            ? { eyebrow: labels.guidedEyebrow, title: labels.guidedTitle, hint: labels.guidedHint, icon: "sparkles" as const }
            : variant === "schedule"
                ? { eyebrow: labels.scheduleEyebrow, title: labels.schedule, hint: labels.scheduleHint, icon: "calendar" as const }
                : variant === "professional"
                    ? { eyebrow: labels.professionalEyebrow, title: labels.professionalTitle, hint: labels.professionalHint, icon: "scissors" as const }
                    : variant === "venue-grid"
                        ? { eyebrow: labels.venueGridEyebrow, title: labels.venueGridTitle, hint: labels.venueGridHint, icon: "calendar" as const }
                        : { eyebrow: labels.quickSlotsEyebrow, title: labels.quickSlotsTitle, hint: labels.quickSlotsHint, icon: "clock" as const };
        return (
            <header className="site-booking__intro">
                <div className="site-booking__intro-copy">
                    <span className="site-booking__eyebrow">{portal?.name ?? content.eyebrow}</span>
                    <h2>{content.title}</h2>
                    <p>{content.hint}</p>
                </div>
                <span className="site-booking__intro-icon"><BookingIcon name={content.icon} /></span>
            </header>
        );
    }

    function loadingNotice() {
        return (
            <div className="site-booking__notice site-booking__loading" role="status">
                <span className="site-booking__spinner" />
                {labels.loading}
            </div>
        );
    }

    function sectionHeading(title: string, hint: string, count?: number) {
        return (
            <div className="site-booking__section-heading">
                <div className="site-booking__section-heading-row">
                    <h2>{title}</h2>
                    {count !== undefined ? <span className="site-booking__count">{count}</span> : null}
                </div>
                <p>{hint}</p>
            </div>
        );
    }

    function serviceCards(items: BookingService[], list = false) {
        if (loading && items.length === 0) return loadingNotice();
        if (items.length === 0) return <p className="site-booking__empty">{labels.noServices}</p>;
        return (
            <div className={list ? "site-booking__service-list" : "site-booking__cards"}>
                {items.map((service) => (
                    <button
                        aria-pressed={serviceUuid === service.uuid}
                        className="site-booking__card"
                        data-selected={serviceUuid === service.uuid}
                        key={service.uuid}
                        onClick={() => chooseService(service)}
                        type="button"
                    >
                        <span className="site-booking__service-icon"><BookingIcon name="sparkles" /></span>
                        <span className="site-booking__card-copy">
                            <strong>{service.name}</strong>
                            <span className="site-booking__meta">
                                <span className="site-booking__meta-item"><BookingIcon name="clock" />{service.durationMinutes} {labels.duration}</span>
                                {showPrices && service.price ? <span className="site-booking__price">{labels.priceFrom} {service.price}</span> : null}
                            </span>
                            {showDescriptions && service.description ? <span className="site-booking__description">{service.description}</span> : null}
                        </span>
                        <span className="site-booking__selection-mark"><BookingIcon name="check" /></span>
                    </button>
                ))}
            </div>
        );
    }

    function resourceCards(items: BookingResource[]) {
        if (loading && items.length === 0) return loadingNotice();
        if (items.length === 0) return <p className="site-booking__empty">{labels.noSlots}</p>;
        return (
            <div className="site-booking__resources">
                {allowAnyResource && variant !== "professional" ? (
                    <button
                        aria-pressed={resourceChosen && resourceUuid === null}
                        className="site-booking__card site-booking__resource site-booking__any-resource"
                        data-selected={resourceChosen && resourceUuid === null}
                        onClick={() => chooseResource(null)}
                        type="button"
                    >
                        <span className="site-booking__avatar"><BookingIcon name="compass" /></span>
                        <span className="site-booking__resource-role">{labels.availability}</span>
                        <strong>{labels.anyProfessional}</strong>
                        <span className="site-booking__description">{labels.anyProfessionalHint}</span>
                    </button>
                ) : null}
                {items.map((resource) => {
                    const serviceCount = services.filter((service) => service.resources.some((item) => item.uuid === resource.uuid)).length;
                    return (
                        <button
                            aria-pressed={resourceChosen && resourceUuid === resource.uuid}
                            className="site-booking__card site-booking__resource"
                            data-selected={resourceChosen && resourceUuid === resource.uuid}
                            key={resource.uuid}
                            onClick={() => chooseResource(resource.uuid)}
                            style={{ "--booking-accent": resource.color ?? "var(--site-primary)" } as CSSProperties}
                            type="button"
                        >
                            <span className="site-booking__avatar">
                                {resource.photoUrl ? (
                                    // Shared templates render public media without Next image optimization.
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img src={resource.photoUrl} alt="" width={64} height={64} style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                                ) : initials(resource.name)}
                            </span>
                            <span className="site-booking__resource-role">{resource.location ?? labels.professional}</span>
                            <strong>{resource.name}</strong>
                            {showDescriptions && resource.description
                                ? <span className="site-booking__description">{resource.description}</span>
                                : <span className="site-booking__meta">{labels.services}: {serviceCount}</span>}
                        </button>
                    );
                })}
            </div>
        );
    }

    function moveCalendar(months: number) {
        const currentMonth = bookingPortalStartOfMonth(today, timezone);
        const nextMonth = bookingPortalAddMonths(visibleMonth, months, timezone);
        if (nextMonth < currentMonth) return;

        const nextDate = nextMonth.getTime() === currentMonth.getTime() ? today : nextMonth;
        setVisibleMonth(nextMonth);
        setSelectedDate(bookingPortalDayKey(nextDate, timezone));
        setSelectedSlot(null);
        setError(null);
    }

    function datePicker(showAvailability = true) {
        const currentMonth = bookingPortalStartOfMonth(today, timezone);
        const previousMonthDisabled = visibleMonth.getTime() <= currentMonth.getTime();
        const availableDates = new Set(resourceFilteredSlotIndex.byDay.keys());
        const visibleMonthKey = bookingPortalDayKey(visibleMonth, timezone).slice(0, 7);

        return (
            <div className="site-booking__date-panel">
                <div className="site-booking__calendar-header">
                    <span className="site-booking__calendar-title">
                        <strong>{labels.chooseDate}</strong>
                        <span aria-live="polite" className="site-booking__calendar-month">
                            {formatPortalDate(visibleMonth, locale, timezone, { month: "long", year: "numeric" })}
                        </span>
                    </span>
                    <span className="site-booking__calendar-navigation">
                        <button
                            aria-label={labels.previousMonth}
                            className="site-booking__calendar-nav"
                            disabled={previousMonthDisabled}
                            onClick={() => moveCalendar(-1)}
                            type="button"
                        >
                            <BookingIcon name="arrow" />
                        </button>
                        <button
                            aria-label={labels.nextMonth}
                            className="site-booking__calendar-nav site-booking__calendar-nav--next"
                            onClick={() => moveCalendar(1)}
                            type="button"
                        >
                            <BookingIcon name="arrow" />
                        </button>
                    </span>
                </div>
                <div aria-hidden="true" className="site-booking__calendar-weekdays">
                    {calendarDates.slice(0, 7).map((date) => (
                        <span className="site-booking__calendar-weekday" key={bookingPortalDayKey(date, timezone)}>
                            {formatPortalDate(date, locale, timezone, { weekday: "short" })}
                        </span>
                    ))}
                </div>
                <div aria-label={labels.chooseDate} className="site-booking__calendar-grid" role="group">
                    {calendarDates.map((date) => {
                        const key = bookingPortalDayKey(date, timezone);
                        const isToday = key === bookingPortalDayKey(today, timezone);
                        const isOutsideMonth = key.slice(0, 7) !== visibleMonthKey;
                        const isPast = date < today;
                        return (
                            <button
                                aria-current={isToday ? "date" : undefined}
                                aria-label={formatPortalDate(date, locale, timezone, { dateStyle: "full" })}
                                aria-pressed={selectedDate === key}
                                className="site-booking__date"
                                data-available={showAvailability && availableDates.has(key)}
                                data-outside-month={isOutsideMonth}
                                data-selected={selectedDate === key}
                                data-today={isToday}
                                disabled={isPast}
                                key={key}
                                onClick={() => {
                                    setSelectedDate(key);
                                    setSelectedSlot(null);
                                    setError(null);
                                    if (isOutsideMonth) {
                                        setVisibleMonth(bookingPortalStartOfMonth(date, timezone));
                                    }
                                }}
                                type="button"
                            >
                                {showAvailability && availableDates.has(key) ? <span className="site-booking__date-dot" /> : null}
                                <strong className="site-booking__date-day">{formatPortalDate(date, locale, timezone, { day: "numeric" })}</strong>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    function timePicker(items: readonly BookingPortalSlotProjection<BookingSlot>[]) {
        if (loading) return loadingNotice();
        if (items.length === 0) return <div className="site-booking__notice">{labels.noSlots}</div>;
        const groups = [
            { label: labels.morning, items: items.filter((slot) => (
                slot.hour < 12
            )) },
            { label: labels.afternoon, items: items.filter((slot) => {
                return slot.hour >= 12 && slot.hour < 17;
            }) },
            { label: labels.evening, items: items.filter((slot) => (
                slot.hour >= 17
            )) },
        ];
        return (
            <div className="site-booking__time-groups">
                {groups.filter((group) => group.items.length > 0).map((group) => (
                    <div className="site-booking__time-group" key={group.label}>
                        <div className="site-booking__time-group-title">{group.label}</div>
                        <div className="site-booking__times">
                            {[...group.items].sort((first, second) => first.startAtMilliseconds - second.startAtMilliseconds).map((projection) => (
                                <button
                                    aria-pressed={sameSlot(selectedSlot, projection.slot)}
                                    className="site-booking__time"
                                    data-selected={sameSlot(selectedSlot, projection.slot)}
                                    key={`${projection.slot.startAt}:${projection.slot.resourceUuid ?? "any"}`}
                                    onClick={() => chooseSlot(projection.slot)}
                                    type="button"
                                >
                                    {formatPortalDate(projection.startAt, locale, timezone, { hour: "2-digit", minute: "2-digit" })}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    function selectionSummary() {
        return (
            <div className="site-booking__summary">
                <div className="site-booking__summary-title"><BookingIcon name="calendar" />{labels.summary}</div>
                {selectedService ? (
                    <div className="site-booking__summary-row">
                        <span>{profile === "restaurant" ? labels.program : labels.chooseService}</span>
                        <strong>{selectedService.name}</strong>
                    </div>
                ) : null}
                {profileContract.capacitySelection === "party-size" ? (
                    <div className="site-booking__summary-row">
                        <span>{labels.partySize}</span>
                        <strong>{partySize}</strong>
                    </div>
                ) : null}
                {requiresResourceChoice
                && (selectedService?.resources.length ?? 0) > 0
                && (resourceChosen || selectedSlot) ? (
                    <div className="site-booking__summary-row">
                        <span>{labels.chooseProfessional}</span>
                        <strong>{selectedSlot?.resourceName ?? selectedResource?.name ?? labels.anyProfessional}</strong>
                    </div>
                ) : null}
                {selectedSlot ? (
                    <div className="site-booking__summary-row">
                        <span>{labels.chooseTime}</span>
                        <strong>{formatPortalDate(new Date(selectedSlot.startAt), locale, timezone, { dateStyle: "medium", timeStyle: "short" })}</strong>
                    </div>
                ) : null}
                {!selectedService && !resourceChosen && !selectedSlot ? <div className="site-booking__summary-empty">{labels.chooseServiceHint}</div> : null}
            </div>
        );
    }

    function scheduleGrid() {
        if (loading) return loadingNotice();
        if (!selectedService) return <div className="site-booking__notice">{labels.noServices}</div>;
        if (filteredSlotProjections.length === 0) return <div className="site-booking__notice">{labels.noSlots}</div>;
        if (
            selectedService.resources.length === 0
            || filteredSlotProjections.some(({ slot }) => slot.resourceUuid === null)
        ) {
            return timePicker(filteredSlotProjections);
        }

        const laneMap = new Map<string, BookingResource>();
        selectedService.resources.forEach((resource) => laneMap.set(resource.uuid, resource));
        const lanes = [...laneMap.values()];
        const times = [...new Set(filteredSlotProjections.map((slot) => slot.hour * 60 + slot.minute))]
            .sort((first, second) => first - second);
        const columnCount = times.length + 1;
        const gridStyle = {
            "--booking-schedule-columns": columnCount,
            gridTemplateColumns: `minmax(158px, 1.2fr) repeat(${times.length}, minmax(78px, .7fr))`,
        } as CSSProperties;

        return (
            <>
                <div className="site-booking__schedule-meta">
                    <div className="site-booking__legend"><span className="site-booking__legend-dot" />{labels.available}</div>
                    <span className="site-booking__count">{labels.availability}: {filteredSlotProjections.length}</span>
                </div>
                <div className="site-booking__schedule-scroll">
                    <div className="site-booking__schedule-grid" role="grid" style={gridStyle}>
                        <div className="site-booking__schedule-cell site-booking__schedule-head site-booking__schedule-corner" role="columnheader">{labels.resource}</div>
                        {times.map((minute) => {
                            const sample = filteredSlotProjections.find((slot) => slot.hour * 60 + slot.minute === minute)!;
                            return <div className="site-booking__schedule-cell site-booking__schedule-head" key={minute} role="columnheader">{formatPortalDate(sample.startAt, locale, timezone, { hour: "2-digit", minute: "2-digit" })}</div>;
                        })}
                        {lanes.flatMap((resource) => [
                            <div className="site-booking__schedule-cell site-booking__schedule-resource" key={`${resource.uuid}:name`} role="rowheader">
                                <span className="site-booking__schedule-resource-copy">
                                    <strong>{resource.name}</strong>
                                    {resource.location || resource.type ? <small>{resource.location ?? resource.type}</small> : null}
                                </span>
                            </div>,
                            ...times.map((minute) => {
                                const projection = filteredSlotProjections.find((item) => (
                                    item.slot.resourceUuid === resource.uuid
                                    && item.hour * 60 + item.minute === minute
                                ));
                                return (
                                    <div className="site-booking__schedule-cell" key={`${resource.uuid}:${minute}`} role="gridcell">
                                        {projection ? (
                                            <button
                                                aria-label={`${resource.name}, ${formatPortalDate(projection.startAt, locale, timezone, { hour: "2-digit", minute: "2-digit" })}`}
                                                aria-pressed={sameSlot(selectedSlot, projection.slot)}
                                                className="site-booking__schedule-slot"
                                                data-selected={sameSlot(selectedSlot, projection.slot)}
                                                onClick={() => chooseSlot(projection.slot)}
                                                type="button"
                                            >
                                                {labels.available}
                                            </button>
                                        ) : <span aria-hidden="true" className="site-booking__schedule-unavailable">—</span>}
                                    </div>
                                );
                            }),
                        ])}
                    </div>
                </div>
            </>
        );
    }

    function backButton(step: BookingPortalFlowStep) {
        return (
            <button className="site-booking__back" onClick={() => isDialogCheckout ? closeDialog() : goToStep(step)} type="button">
                <BookingIcon name="arrow" />{labels.back}
            </button>
        );
    }

    function guidedContent() {
        if (currentStage === "service") {
            return <>{sectionHeading(labels.chooseService, labels.chooseServiceHint, services.length)}{serviceCards(services)}</>;
        }
        if (currentStage === "resource" && selectedService) {
            return <>{backButton("service")}{sectionHeading(labels.chooseProfessional, labels.chooseProfessionalHint)}{resourceCards(selectedService.resources)}</>;
        }
        if (currentStage === "schedule" && selectedService) {
            return <>{backButton(requiresResourceChoice ? "resource" : "service")}{sectionHeading(labels.chooseTime, labels.guidedHint, filteredSlotProjections.length)}{datePicker()}{timePicker(filteredSlotProjections)}</>;
        }
        return null;
    }

    function professionalContent() {
        if (currentStage === "resource") {
            return <>{sectionHeading(labels.chooseProfessional, labels.chooseProfessionalHint, resources.length)}{resourceCards(resources)}</>;
        }
        if (currentStage === "service") {
            return <>{backButton("resource")}{sectionHeading(labels.chooseService, selectedResource?.name ?? labels.chooseServiceHint, availableServices.length)}{serviceCards(availableServices, true)}</>;
        }
        if (currentStage === "schedule" && selectedService) {
            return <>{backButton("service")}{sectionHeading(labels.chooseTime, `${selectedResource?.name ?? labels.anyProfessional} · ${selectedService.name}`, filteredSlotProjections.length)}{datePicker()}{timePicker(filteredSlotProjections)}</>;
        }
        return null;
    }

    function scheduleContent() {
        return (
            <>
                {sectionHeading(labels.schedule, labels.scheduleHint)}
                <div className="site-booking__schedule-toolbar">
                    <div className="site-booking__select-wrap">
                        <label htmlFor={`${block.uuid}-service`}>{labels.chooseService}</label>
                        <select
                            className="site-booking__select"
                            id={`${block.uuid}-service`}
                            onChange={(event) => {
                                setServiceUuid(event.target.value || null);
                                setSelectedSlot(null);
                                setError(null);
                            }}
                            value={serviceUuid ?? ""}
                        >
                            <option disabled value="">{labels.chooseService}</option>
                            {services.map((service) => <option key={service.uuid} value={service.uuid}>{service.name}</option>)}
                        </select>
                    </div>
                    {datePicker()}
                </div>
                {scheduleGrid()}
            </>
        );
    }

    function restaurantControls() {
        return (
            <div className="site-booking__restaurant-toolbar">
                <label className="site-booking__party-size" htmlFor={`${block.uuid}-party-size`}>
                    <span>{labels.partySize}</span>
                    <select
                        id={`${block.uuid}-party-size`}
                        onChange={(event) => {
                            setPartySize(Number(event.target.value));
                            setSelectedSlot(null);
                            setError(null);
                        }}
                        value={partySize}
                    >
                        {Array.from({ length: 20 }, (_, index) => index + 1).map((value) => (
                            <option key={value} value={value}>{value}</option>
                        ))}
                    </select>
                </label>
                {services.length > 1 ? (
                    <label className="site-booking__party-size" htmlFor={`${block.uuid}-restaurant-service`}>
                        <span>{labels.program}</span>
                        <select
                            id={`${block.uuid}-restaurant-service`}
                            onChange={(event) => {
                                setServiceUuid(event.target.value);
                                setSelectedSlot(null);
                                setError(null);
                            }}
                            value={serviceUuid ?? ""}
                        >
                            {services.map((service) => (
                                <option key={service.uuid} value={service.uuid}>{service.name}</option>
                            ))}
                        </select>
                    </label>
                ) : null}
            </div>
        );
    }

    function restaurantQuickAvailability() {
        const upcoming = [...resourceFilteredSlotIndex.slots]
            .sort((first, second) => first.startAtMilliseconds - second.startAtMilliseconds)
            .slice(0, 18);
        const groups = new Map<string, BookingPortalSlotProjection<BookingSlot>[]>();
        upcoming.forEach((projection) => {
            groups.set(projection.dayKey, [...(groups.get(projection.dayKey) ?? []), projection]);
        });

        if (loading) return loadingNotice();
        if (upcoming.length === 0) {
            return <div className="site-booking__notice">{labels.noPartyAvailability}</div>;
        }

        return (
            <section className="site-booking__quick-feed">
                {[...groups].map(([day, items]) => (
                    <article className="site-booking__quick-day" data-booking-day={day} key={day}>
                        <header>
                            <span>{formatPortalDate(bookingPortalDateFromDayKey(day, timezone), locale, timezone, { weekday: "long" })}</span>
                            <strong>{formatPortalDate(bookingPortalDateFromDayKey(day, timezone), locale, timezone, { dateStyle: "medium" })}</strong>
                        </header>
                        <div>
                            {items.map((projection) => (
                                <button
                                    key={`${projection.slot.startAt}:${projection.slot.resourceUuid ?? "any"}`}
                                    onClick={() => chooseSlot(projection.slot)}
                                    type="button"
                                >
                                    <strong>{formatPortalDate(projection.startAt, locale, timezone, { hour: "2-digit", minute: "2-digit" })}</strong>
                                </button>
                            ))}
                        </div>
                    </article>
                ))}
            </section>
        );
    }

    function restaurantContent() {
        if (!selectedService) {
            if (loading) return loadingNotice();
            return <div className="site-booking__notice">{labels.noServices}</div>;
        }

        const hasCapacityBearingResource = selectedService.resources.some((resource) => (
            typeof resource.capacity === "number" && resource.capacity >= partySize
        ));
        const availability = variant === "quick-slots"
            ? restaurantQuickAvailability()
            : (
                <>
                    {datePicker()}
                    {loading
                        ? loadingNotice()
                        : filteredSlotProjections.length > 0
                            ? timePicker(filteredSlotProjections)
                            : <div className="site-booking__notice">{labels.noPartyAvailability}</div>}
                </>
            );

        return (
            <div className="site-booking__restaurant-flow">
                {sectionHeading(labels.chooseTime, labels.partySizeHint)}
                {hasCapacityBearingResource ? (
                    variant === "schedule" ? (
                        <>
                            <div className="site-booking__schedule-toolbar">
                                {restaurantControls()}
                                {datePicker()}
                            </div>
                            {loading
                                ? loadingNotice()
                                : filteredSlotProjections.length > 0
                                    ? timePicker(filteredSlotProjections)
                                    : <div className="site-booking__notice">{labels.noPartyAvailability}</div>}
                        </>
                    ) : <>{restaurantControls()}{availability}</>
                ) : (
                    <>{restaurantControls()}<div className="site-booking__notice">{labels.capacitySetupRequired}</div></>
                )}
            </div>
        );
    }

    function bookingForm() {
        if (!selectedService || !selectedSlot) return null;
        return (
            <div className="site-booking__checkout">
                <div className="site-booking__checkout-summary">
                    {backButton("schedule")}
                    <h2>{labels.contactDetails}</h2>
                    <p className="site-booking__checkout-copy">{labels.contactHint}</p>
                    {selectionSummary()}
                </div>
                <form className="site-booking__checkout-form" onSubmit={submit}>
                    <div className="site-booking__form">
                        <div className="site-booking__field">
                            <label htmlFor={`${block.uuid}-name`}>{labels.name}</label>
                            <input id={`${block.uuid}-name`} onChange={(event) => setContact((current) => ({ ...current, name: event.target.value }))} ref={isDialogCheckout ? dialogNameRef : undefined} required value={contact.name} />
                        </div>
                        <div className="site-booking__field">
                            <label htmlFor={`${block.uuid}-email`}>{labels.email}</label>
                            <input id={`${block.uuid}-email`} onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))} required type="email" value={contact.email} />
                        </div>
                        <div className="site-booking__field">
                            <label htmlFor={`${block.uuid}-phone`}>{labels.phone}</label>
                            <input id={`${block.uuid}-phone`} onChange={(event) => setContact((current) => ({ ...current, phone: event.target.value }))} type="tel" value={contact.phone} />
                        </div>
                        <div className="site-booking__field site-booking__field--wide">
                            <label htmlFor={`${block.uuid}-notes`}>{labels.notes}</label>
                            <textarea id={`${block.uuid}-notes`} onChange={(event) => setContact((current) => ({ ...current, notes: event.target.value }))} rows={3} value={contact.notes} />
                        </div>
                    </div>
                    {error ? <div className="site-booking__notice site-booking__notice--error" role="alert">{error}</div> : null}
                    <div className="site-booking__actions">
                        <button className="site-booking__button site-booking__button--secondary" onClick={() => isDialogCheckout ? closeDialog() : goToStep("schedule")} type="button">{labels.change}</button>
                        <button className="site-booking__button" disabled={submitting || editorMode || readOnly} type="submit">
                            <BookingIcon name="check" />{submitting ? labels.loading : labels.confirm}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    function bookingDialog() {
        if (!isDialogCheckout || selectedService === null || selectedSlot === null) return null;
        return (
            <div
                aria-labelledby={`${block.uuid}-booking-dialog-title`}
                aria-modal="true"
                className="site-booking__dialog-backdrop"
                onMouseDown={(event) => {
                    if (event.currentTarget === event.target) closeDialog();
                }}
                role="dialog"
            >
                <div className="site-booking__dialog-panel" ref={dialogPanelRef}>
                    <button
                        aria-label={labels.close}
                        className="site-booking__dialog-close"
                        onClick={closeDialog}
                        type="button"
                    >
                        ×
                    </button>
                    <h2 className="site-booking__dialog-title" id={`${block.uuid}-booking-dialog-title`}>{labels.contactDetails}</h2>
                    {bookingForm()}
                </div>
            </div>
        );
    }

    function venueServiceRail() {
        if (loading && services.length === 0) return loadingNotice();
        return (
            <aside className="site-booking__venue-rail">
                <div className="site-booking__venue-rail-heading">
                    <span>1</span>
                    <strong>{labels.chooseService}</strong>
                </div>
                <div className="site-booking__venue-services">
                    {services.map((service) => (
                        <button
                            aria-pressed={service.uuid === serviceUuid}
                            data-selected={service.uuid === serviceUuid}
                            key={service.uuid}
                            onClick={() => chooseService(service)}
                            type="button"
                        >
                            <span className="site-booking__service-icon"><BookingIcon name="sparkles" /></span>
                            <span>
                                <strong>{service.name}</strong>
                                <small>{service.durationMinutes} {labels.duration}{showPrices && service.price ? ` · ${labels.priceFrom} ${service.price}` : ""}</small>
                            </span>
                            <BookingIcon name="check" />
                        </button>
                    ))}
                </div>
                {selectedService && selectedService.resources.length > 0 ? (
                    <>
                        <div className="site-booking__venue-rail-heading">
                            <span>2</span>
                            <strong>{labels.resource}</strong>
                        </div>
                        <label className="site-booking__venue-resource-select">
                            <span>{labels.resource}</span>
                            <select
                                onChange={(event) => chooseResource(
                                    event.target.value,
                                )}
                                value={resourceUuid ?? ""}
                            >
                                {selectedService.resources.map((resource) => (
                                    <option key={resource.uuid} value={resource.uuid}>
                                        {resource.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </>
                ) : null}
            </aside>
        );
    }

    function weekNavigation() {
        const currentWeek = bookingPortalStartOfWeek(today, timezone);
        const previousDisabled = visibleWeek.getTime() <= currentWeek.getTime();
        const moveWeek = (days: number) => {
            const nextWeek = bookingPortalAddDays(visibleWeek, days, timezone);
            setVisibleWeek(nextWeek);
            setSelectedDate(bookingPortalDayKey(nextWeek < today ? today : nextWeek, timezone));
            setVisibleMonth(bookingPortalStartOfMonth(nextWeek, timezone));
        };
        return (
            <div className="site-booking__week-navigation">
                <div>
                    <span>{labels.chooseTime}</span>
                    <strong>{formatPortalDate(weekDates[0]!, locale, timezone, { dateStyle: "medium" })} – {formatPortalDate(weekDates[6]!, locale, timezone, { dateStyle: "medium" })}</strong>
                </div>
                <span>
                    <button aria-label={labels.previousWeek} disabled={previousDisabled} onClick={() => moveWeek(-7)} type="button"><BookingIcon name="arrow" /></button>
                    <button aria-label={labels.nextWeek} onClick={() => moveWeek(7)} type="button"><BookingIcon name="arrow" /></button>
                </span>
            </div>
        );
    }

    function venueDateJump() {
        const currentWeekKey = bookingPortalDayKey(visibleWeek, timezone);
        const selectedDay = bookingPortalDateFromDayKey(selectedDate, timezone);
        const selectedWeek = bookingPortalStartOfWeek(selectedDay, timezone);
        return (
            <div className="site-booking__venue-date-jump">
                <button
                    aria-expanded={dateJumpOpen}
                    className="site-booking__date-jump-toggle"
                    onClick={() => setDateJumpOpen((open) => !open)}
                    type="button"
                >
                    <BookingIcon name="calendar" />
                    <span>{labels.chooseDate}</span>
                    <strong>{formatPortalDate(selectedDay, locale, timezone, { dateStyle: "medium" })}</strong>
                </button>
                {dateJumpOpen ? (
                    <div className="site-booking__date-jump-popover">
                        {datePicker(false)}
                        <button
                            className="site-booking__button site-booking__button--secondary"
                            disabled={bookingPortalDayKey(selectedWeek, timezone) === currentWeekKey}
                            onClick={() => {
                                setVisibleWeek(selectedWeek);
                                setDateJumpOpen(false);
                            }}
                            type="button"
                        >
                            {labels.schedule}
                        </button>
                    </div>
                ) : null}
            </div>
        );
    }

    function venueWeekGrid() {
        if (!selectedService) return <div className="site-booking__notice">{labels.chooseServiceHint}</div>;
        const hours = Array.from({ length: 16 }, (_, index) => index + 6);
        const currentDayKey = bookingPortalDayKey(currentInstant, timezone);
        const currentHour = bookingPortalTimeParts(currentInstant, timezone).hour;
        const resourceName = selectedResource?.name ?? labels.resource;
        return (
            <div className="site-booking__venue-grid-scroll">
                <div className="site-booking__venue-grid" role="grid">
                    <div className="site-booking__venue-grid-corner" role="columnheader">{labels.schedule}</div>
                    {weekDates.map((date) => (
                        <div aria-current={bookingPortalDayKey(date, timezone) === bookingPortalDayKey(today, timezone) ? "date" : undefined} className="site-booking__venue-grid-day" data-current={bookingPortalDayKey(date, timezone) === bookingPortalDayKey(today, timezone)} key={bookingPortalDayKey(date, timezone)} role="columnheader">
                            <span>{formatPortalDate(date, locale, timezone, { weekday: "short" })}</span>
                            <strong>{formatPortalDate(date, locale, timezone, { day: "numeric", month: "numeric" })}</strong>
                        </div>
                    ))}
                    {hours.flatMap((hour) => [
                        <div className="site-booking__venue-grid-time" key={`time:${hour}`} role="rowheader">
                            {String(hour).padStart(2, "0")}:00
                        </div>,
                        ...weekDates.map((date) => {
                            const dayKey = bookingPortalDayKey(date, timezone);
                            const bucket = resourceFilteredSlotIndex.byDayHour.get(
                                bookingPortalDayHourKey(dayKey, hour),
                            ) ?? [];
                            const isPast = dayKey < currentDayKey
                                || (dayKey === currentDayKey && hour < currentHour);
                            const stateLabel = isPast ? labels.past : labels.unavailableSlot;
                            return (
                                <div className="site-booking__venue-grid-cell" data-booking-day={dayKey} data-state={bucket.length > 0 ? "available" : isPast ? "past" : "unavailable"} key={`${dayKey}:${hour}`} role="gridcell">
                                    {bucket.map((projection) => (
                                        <button
                                            aria-label={`${resourceName}, ${formatPortalDate(projection.startAt, locale, timezone, { dateStyle: "medium", timeStyle: "short" })}, ${labels.available}`}
                                            className="site-booking__venue-slot"
                                            disabled={loading}
                                            key={`${projection.slot.startAt}:${projection.slot.resourceUuid ?? "any"}`}
                                            onClick={(event) => chooseSlot(projection.slot, event.currentTarget)}
                                            type="button"
                                        >
                                            <strong>{formatPortalDate(projection.startAt, locale, timezone, { hour: "2-digit", minute: "2-digit" })}</strong>
                                        </button>
                                    ))}
                                    {bucket.length === 0 ? <span aria-label={`${String(hour).padStart(2, "0")}:00, ${stateLabel}`} className="site-booking__venue-unavailable">{stateLabel}</span> : null}
                                </div>
                            );
                        }),
                    ])}
                </div>
            </div>
        );
    }

    function venueMobileAgenda() {
        if (!selectedService) return null;
        const mobileDay = weekDates.find((date) => (
            bookingPortalDayKey(date, timezone) === selectedDate
        )) ?? weekDates[0]!;
        const dayKey = bookingPortalDayKey(mobileDay, timezone);
        const daySlots = [...(resourceFilteredSlotIndex.byDay.get(dayKey) ?? [])]
            .sort((first, second) => first.startAtMilliseconds - second.startAtMilliseconds);
        return (
            <div className="site-booking__venue-mobile">
                <div aria-label={labels.chooseDate} className="site-booking__venue-day-tabs" role="group">
                    {weekDates.map((date) => {
                        const key = bookingPortalDayKey(date, timezone);
                        return (
                            <button aria-pressed={key === dayKey} data-selected={key === dayKey} key={key} onClick={() => setSelectedDate(key)} type="button">
                                <span>{formatPortalDate(date, locale, timezone, { weekday: "short" })}</span>
                                <strong>{formatPortalDate(date, locale, timezone, { day: "numeric" })}</strong>
                            </button>
                        );
                    })}
                </div>
                <div className="site-booking__venue-agenda">
                    {daySlots.map((projection) => (
                        <button
                            aria-label={`${formatPortalDate(projection.startAt, locale, timezone, { dateStyle: "full", timeStyle: "short" })}, ${projection.slot.resourceName ?? selectedResource?.name ?? labels.available}`}
                            disabled={loading}
                            key={`${projection.slot.startAt}:${projection.slot.resourceUuid ?? "any"}`}
                            onClick={(event) => chooseSlot(projection.slot, event.currentTarget)}
                            type="button"
                        >
                            <strong>{formatPortalDate(projection.startAt, locale, timezone, { hour: "2-digit", minute: "2-digit" })}</strong>
                            {projection.slot.resourceName || selectedResource?.name ? (
                                <span>{projection.slot.resourceName ?? selectedResource?.name}</span>
                            ) : null}
                            <small>{labels.available}</small>
                        </button>
                    ))}
                    {daySlots.length === 0 ? <div className="site-booking__notice">{labels.noSlots}</div> : null}
                </div>
            </div>
        );
    }

    // Contact field updates should not reconcile the full 112-cell desktop grid.
    // Its element tree only changes with timetable data or calendar selection.
    const memoizedVenueWeekGrid = useMemo(() => venueWeekGrid(), [
        currentInstant,
        labels,
        loading,
        resourceFilteredSlotIndex,
        selectedResource,
        selectedService,
        timezone,
        today,
        weekDates,
    ]);

    function venueGridContent() {
        const resourcesUnavailable = selectedService !== null
            && selectedService.resources.length === 0;

        return (
            <div className="site-booking__venue-layout">
                {venueServiceRail()}
                <section aria-busy={slotsLoading} className="site-booking__venue-schedule">
                    {resourcesUnavailable ? (
                        <div className="site-booking__notice">{labels.noResources}</div>
                    ) : (
                        <>
                            {weekNavigation()}
                            {venueDateJump()}
                            {memoizedVenueWeekGrid}
                            {venueMobileAgenda()}
                        </>
                    )}
                </section>
            </div>
        );
    }

    function quickSlotsContent() {
        const upcoming = [...resourceFilteredSlotIndex.slots]
            .sort((first, second) => first.startAtMilliseconds - second.startAtMilliseconds)
            .slice(0, 18);
        const groups = new Map<string, BookingPortalSlotProjection<BookingSlot>[]>();
        upcoming.forEach((projection) => {
            groups.set(projection.dayKey, [...(groups.get(projection.dayKey) ?? []), projection]);
        });
        return (
            <div className="site-booking__quick-layout">
                <div className="site-booking__quick-services" role="group" aria-label={labels.chooseService}>
                    {services.map((service) => (
                        <button
                            aria-pressed={service.uuid === serviceUuid}
                            data-selected={service.uuid === serviceUuid}
                            key={service.uuid}
                            onClick={() => chooseService(service)}
                            type="button"
                        >
                            <strong>{service.name}</strong>
                            <span>{service.durationMinutes} {labels.duration}{showPrices && service.price ? ` · ${labels.priceFrom} ${service.price}` : ""}</span>
                        </button>
                    ))}
                </div>
                <section aria-busy={slotsLoading} className="site-booking__quick-feed">
                    {slotsLoading && upcoming.length === 0 ? loadingNotice() : null}
                    {!slotsLoading && upcoming.length === 0 ? <div className="site-booking__notice">{labels.noSlots}</div> : null}
                    {[...groups].map(([day, items]) => (
                        <article className="site-booking__quick-day" data-booking-day={day} key={day}>
                            <header>
                                <span>{formatPortalDate(bookingPortalDateFromDayKey(day, timezone), locale, timezone, { weekday: "long" })}</span>
                                <strong>{formatPortalDate(bookingPortalDateFromDayKey(day, timezone), locale, timezone, { dateStyle: "medium" })}</strong>
                            </header>
                            <div>
                                {items.map((projection) => (
                                    <button
                                        aria-label={`${formatPortalDate(projection.startAt, locale, timezone, { dateStyle: "full", timeStyle: "short" })}${projection.slot.resourceName ? `, ${projection.slot.resourceName}` : ""}, ${labels.available}`}
                                        key={`${projection.slot.startAt}:${projection.slot.resourceUuid ?? "any"}`}
                                        onClick={(event) => chooseSlot(projection.slot, event.currentTarget)}
                                        type="button"
                                    >
                                        <strong>{formatPortalDate(projection.startAt, locale, timezone, { hour: "2-digit", minute: "2-digit" })}</strong>
                                        {projection.slot.resourceName ? <span>{projection.slot.resourceName}</span> : null}
                                    </button>
                                ))}
                            </div>
                        </article>
                    ))}
                </section>
            </div>
        );
    }

    if (!previewMode && portal === null) {
        return (
            <div className="site-booking">
                <style>{BOOKING_PORTAL_CSS}</style>
                <div className="site-booking__notice">{labels.unavailable}</div>
            </div>
        );
    }

    if (!previewMode && portal !== null && !servicesLoading && services.length === 0) {
        return (
            <div className="site-booking">
                <style>{BOOKING_PORTAL_CSS}</style>
                <div
                    className={`site-booking__notice${error ? " site-booking__notice--error" : ""}`}
                    role={error ? "alert" : "status"}
                >
                    {error ?? labels.noServices}
                </div>
            </div>
        );
    }

    return (
        <div className="site-booking" data-presentation="page" data-profile={profile} data-variant={variant} ref={rootRef}>
            <style>{BOOKING_PORTAL_CSS}</style>
            <div className="site-booking__shell">
                {success ? (
                    <div className="site-booking__success" role="status">
                        <span className="site-booking__success-icon"><BookingIcon name="check" /></span>
                        <h2>{labels.bookingSuccess}</h2>
                        <p>{labels.bookingSuccessHint}</p>
                        <button className="site-booking__button site-booking__button--secondary" onClick={() => { setSuccess(false); setSelectedSlot(null); }} type="button">{labels.reset}</button>
                    </div>
                ) : (
                    <>
                        {intro()}
                        {!isDialogCheckout && profile !== "restaurant" ? progress() : null}
                        <div className="site-booking__body">
                            {!isDialogCheckout && currentStage === "contact" ? bookingForm() : (
                                <div className="site-booking__workspace" data-with-summary={!isDialogCheckout && variant !== "schedule" && (selectedService !== null || resourceChosen)}>
                                    <main className="site-booking__main">
                                        {profile === "restaurant" ? restaurantContent() : null}
                                        {profile !== "restaurant" && variant === "guided" ? guidedContent() : null}
                                        {profile !== "restaurant" && variant === "schedule" ? scheduleContent() : null}
                                        {profile !== "restaurant" && variant === "professional" ? professionalContent() : null}
                                        {profile !== "restaurant" && variant === "venue-grid" ? venueGridContent() : null}
                                        {profile !== "restaurant" && variant === "quick-slots" ? quickSlotsContent() : null}
                                        {error ? <div className="site-booking__notice site-booking__notice--error" role="alert">{error}</div> : null}
                                    </main>
                                    {!isDialogCheckout && variant !== "schedule" && (selectedService !== null || resourceChosen) ? <aside className="site-booking__aside">{selectionSummary()}</aside> : null}
                                </div>
                            )}
                        </div>
                        {bookingDialog()}
                    </>
                )}
            </div>
        </div>
    );
}
